import { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Divider } from "primereact/divider";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import PageTitle from "../common/PageTitle";
import TabHeader from "../common/TabHeader";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { TEXT_NORMAL, TEXT_SMALL } from "../../style";
import FieldNote from "./common/FieldNote";

export default function WebsiteNotificationsPage() {
    const { requestAPI, showToast } = useAppContext();
    const [loading, setLoading] = useState();
    const [saving, setSaving] = useState();
    const [error, setError] = useState();
    const [record, setRecord] = useState();

    useEffect(() => {
        requestAPI({
            requestPath: "website/notifications-page",
            requestMethod: "GET",
            setLoading,
            onRequestStart: setError,
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) setRecord(data);
                else setError("Couldn't load Notifications Page");
            },
        });
    }, [requestAPI]);

    const saveRecord = () => {
        requestAPI({
            requestPath: "website/notifications-page",
            requestMethod: "PATCH",
            requestPostBody: record,
            setLoading: setSaving,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Update Notifications Page !", life: 2000 }),
            onResponseReceieved: (updated, responseCode) => {
                if (updated && responseCode === 200) {
                    setRecord(updated);
                    showToast({ severity: "success", summary: "Updated", detail: "Notifications Page Updated", life: 1000 });
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: updated?.error || "Failed To Update Notifications Page !", life: 2000 });
                }
            },
        });
    };

    return (
        <div className="flex flex-column h-full">
            <PageTitle title="Notifications Page" />
            <TabHeader
                className="px-3 pt-3"
                title="Notifications page text"
                highlights={["These words appear at the top of the Notifications page, above the notices"]}
            />
            <Divider />
            {loading ? <Loading /> : null}
            {error ? <NoContent error={error} /> : null}
            {!loading && !error && record ? (
                <div className="flex-1 overflow-y-scroll p-3 flex flex-column">
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="eyebrow" className="w-full" value={record?.eyebrow ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, eyebrow: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="eyebrow">Small line above the heading</label>
                        </FloatLabel>
                        <FieldNote text="Example: SAHAS Updates." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="title" className="w-full" value={record?.title ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, title: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="title">Page heading</label>
                        </FloatLabel>
                        <FieldNote text="The large heading at the top of the Notifications page." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputTextarea id="description" className="w-full" rows={4} autoResize value={record?.description || ""} onChange={(event) => setRecord((prev) => ({ ...prev, description: event.target.value }))} disabled={saving} />
                            <label htmlFor="description">Line under the heading</label>
                        </FloatLabel>
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="list_eyebrow" className="w-full" value={record?.list_eyebrow ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, list_eyebrow: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="list_eyebrow">Small line above the notices</label>
                        </FloatLabel>
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="list_title" className="w-full" value={record?.list_title ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, list_title: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="list_title">Heading above the notices</label>
                        </FloatLabel>
                        <FieldNote text="Example: All Notifications." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputTextarea id="list_description" className="w-full" rows={3} autoResize value={record?.list_description || ""} onChange={(event) => setRecord((prev) => ({ ...prev, list_description: event.target.value }))} disabled={saving} />
                            <label htmlFor="list_description">Line under the notices heading</label>
                        </FloatLabel>
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="empty_title" className="w-full" value={record?.empty_title ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, empty_title: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="empty_title">Heading when there are no notices</label>
                        </FloatLabel>
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputTextarea id="empty_text" className="w-full" rows={3} autoResize value={record?.empty_text || ""} onChange={(event) => setRecord((prev) => ({ ...prev, empty_text: event.target.value }))} disabled={saving} />
                            <label htmlFor="empty_text">Message when there are no notices</label>
                        </FloatLabel>
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="search_empty_title" className="w-full" value={record?.search_empty_title ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, search_empty_title: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="search_empty_title">Heading when search finds nothing</label>
                        </FloatLabel>
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputTextarea id="search_empty_text" className="w-full" rows={3} autoResize value={record?.search_empty_text || ""} onChange={(event) => setRecord((prev) => ({ ...prev, search_empty_text: event.target.value }))} disabled={saving} />
                            <label htmlFor="search_empty_text">Message when search finds nothing</label>
                        </FloatLabel>
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="load_more_text" className="w-full" value={record?.load_more_text ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, load_more_text: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="load_more_text">Load more button</label>
                        </FloatLabel>
                        <FieldNote text="Shown when there are more notices than the number below." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="page_size" className="w-full" value={record?.page_size ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, page_size: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="page_size">Notices shown at first</label>
                        </FloatLabel>
                        <FieldNote text="Example: 6. The rest appear after Load more." />
                    </div>
                    <div className="flex align-items-center gap-2 mt-4">
                        <Checkbox inputId="show_search" checked={!!record?.show_search} onChange={(event) => setRecord((prev) => ({ ...prev, show_search: event.checked }))} disabled={saving} />
                        <label htmlFor="show_search" className={TEXT_SMALL}>Show the search box</label>
                    </div>
                    <div className="flex align-items-center gap-2 mt-4">
                        <Checkbox inputId="show_filters" checked={!!record?.show_filters} onChange={(event) => setRecord((prev) => ({ ...prev, show_filters: event.checked }))} disabled={saving} />
                        <label htmlFor="show_filters" className={TEXT_SMALL}>Show course and category filters</label>
                    </div>
                    <div className="flex align-items-center gap-2 mt-4">
                        <Checkbox inputId="show_sort" checked={!!record?.show_sort} onChange={(event) => setRecord((prev) => ({ ...prev, show_sort: event.checked }))} disabled={saving} />
                        <label htmlFor="show_sort" className={TEXT_SMALL}>Show newest and oldest sorting</label>
                    </div>
                    <Button className="mt-5 align-self-start" label="Save" icon="pi pi-check" loading={saving} onClick={saveRecord} />
                </div>
            ) : null}
        </div>
    );
}
